import React, { useState, useEffect } from "react";
import { Button, Col, Row, Card, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ReduxState } from "../../../redux";
import {
  resumeJob,
  getOveragePricing,
  loadPreferenceServer,
  savePreferenceServer,
} from "../../../docuclipper/api";
import { setJobData } from "../../../redux/reducers/JobData";
import { createAlert } from "../../../redux/reducers/Alerts";
import { openModal } from "../../../redux/reducers/ModalManager";
import { getNextUpgradeOption } from "../../Pages/Account/SelectPlanAfterTrial/tierUtils";

export const JobOutOfPages = ({ job }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { subscription } = useSelector(
    (state: ReduxState) => state.Subscription,
  );
  const { plans } = useSelector((state: ReduxState) => state.Plans);
  const { user } = useSelector((state: ReduxState) => state.User);
  const neededPages = useSelector(
    (state: ReduxState) => state.JobData.job?.totalJobs,
  );

  const userPages = Number(user?.credits) + Number(user?.purchasedCredits);
  const includedPages = subscription?.docuclipperPlan?.numPages ?? 0;
  const multiplier =
    includedPages > 0 && neededPages
      ? Math.max(1, Math.round(Number(neededPages) / includedPages))
      : null;

  const [overagePricePerPage, setOveragePricePerPage] = useState<number | null>(
    null,
  );
  const [overageLimit, setOverageLimit] = useState<"unlimited" | number>(
    "unlimited",
  );
  const [overageConfigLoaded, setOverageConfigLoaded] = useState(false);
  const [currentOverageLimit, setCurrentOverageLimit] = useState<
    number | null | "unlimited"
  >(null);
  const [confirmingOverage, setConfirmingOverage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadPreferenceServer({ key: "overageConfig" })
      .then((pref) => {
        if (cancelled || !pref?.value) {
          setCurrentOverageLimit(200);
          setOverageConfigLoaded(true);
          return;
        }
        try {
          const obj = JSON.parse(pref.value);
          const pct = obj.limitPercent;
          setCurrentOverageLimit(
            pct === undefined || pct === null || pct === "unlimited"
              ? "unlimited"
              : pct,
          );
        } catch {
          setCurrentOverageLimit(200);
        }
        setOverageConfigLoaded(true);
      })
      .catch(() => setOverageConfigLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const nextUpgrade =
    subscription?.status === "active" && plans?.length > 0 && includedPages > 0
      ? getNextUpgradeOption(
          plans,
          includedPages,
          (subscription.docuclipperPlan.billingCycle as
            | "Monthly"
            | "Annually"
            | "AnnuallyBilledMonthly") || "Monthly",
        )
      : null;

  useEffect(() => {
    let cancelled = false;
    getOveragePricing()
      .then((r) => {
        if (!cancelled && r?.pricePerPage != null)
          setOveragePricePerPage(r.pricePerPage);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    dispatch(
      createAlert({
        timeout: 0,
        type: "info",
        messageWithUpdate: `You've processed ${multiplier ? `${multiplier}×` : ""} your plan's included pages. Choose the best option below to continue.`,
        message: "",
      }),
    );
  }, [dispatch, multiplier]);

  const onConfirmOverageAndResume = async () => {
    if (!job) return;
    setConfirmingOverage(true);
    try {
      await savePreferenceServer({
        key: "overageConfig",
        value: JSON.stringify({
          enabled: true,
          limitPercent: overageLimit === "unlimited" ? null : overageLimit,
        }),
      });
      onResume();
    } catch (err) {
      dispatch(
        createAlert({
          message: "Failed to save extended limit setting",
          timeout: 0,
          type: "error",
        }),
      );
    } finally {
      setConfirmingOverage(false);
    }
  };

  const onResume = () => {
    if (!job) return;
    resumeJob(job.id)
      .then((rsp) => {
        dispatch(setJobData({ job: rsp, progress: null }));
        dispatch(
          createAlert({
            message: "Job resumed successfully",
            timeout: 0,
            type: "success",
          }),
        );
      })
      .catch((err) => {
        dispatch(
          createAlert({
            message: "Error resuming job",
            timeout: 0,
            type: "error",
          }),
        );
      });
  };

  const estimatedOverageCost =
    overagePricePerPage != null &&
    neededPages != null &&
    includedPages > 0 &&
    neededPages > includedPages
      ? (neededPages - includedPages) * overagePricePerPage
      : null;

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto" }}>
      <Row className="text-center mb-4">
        <Col>
          <p className="h5 text-muted mb-2">
            You've processed
            {multiplier != null ? ` ${multiplier}×` : ""} your plan's included
            pages.
          </p>
          <p className="lead mb-3">
            Let's choose the best option for your continued processing.
          </p>
          <Button variant="outline-secondary" size="sm" onClick={onResume}>
            Resume job
          </Button>
        </Col>
      </Row>

      <Row className="mb-4">
        {/* Option 1 — Upgrade Plan (Primary) */}
        <Col md={12} lg={4} className="mb-3">
          <Card className="h-100 border-success" style={{ borderWidth: 2 }}>
            <Card.Body>
              <div className="d-flex align-items-center mb-2">
                <span
                  className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center mr-2"
                  style={{ width: 28, height: 28, fontSize: 14 }}
                >
                  1
                </span>
                <Card.Title className="mb-0 text-success">
                  Upgrade Plan
                </Card.Title>
              </div>
              <p className="small text-muted mb-3">
                Best for most users. Lower your cost per page.
              </p>
              {nextUpgrade ? (
                <>
                  {estimatedOverageCost != null && estimatedOverageCost > 0 && (
                    <p className="small font-weight-bold text-success mb-2">
                      Save ~${estimatedOverageCost.toFixed(0)} this month
                    </p>
                  )}
                  <Button
                    variant="success"
                    className="w-100 mb-2"
                    onClick={() => {
                      const planName = encodeURIComponent(
                        nextUpgrade.plan.docuclipperPlan.name as string,
                      );
                      const cycle =
                        nextUpgrade.plan.docuclipperPlan.billingCycle ||
                        "Monthly";
                      const returnUrl =
                        typeof window !== "undefined" && job?.id
                          ? `${window.location.origin}/jobData?jobId=${job.id}&resumeJob=1`
                          : "";
                      const nextParam = returnUrl
                        ? `&next=${encodeURIComponent(returnUrl)}`
                        : "";
                      history.push(
                        `/account/upgrade?upgradeTo=${planName}&billingCycle=${cycle}${nextParam}`,
                      );
                    }}
                  >
                    Upgrade to {nextUpgrade.displayName}
                  </Button>
                  <p className="small mb-0">
                    {nextUpgrade.numPages.toLocaleString()} pages/mo at{" "}
                    {nextUpgrade.priceDisplay}
                  </p>
                </>
              ) : (
                <>
                  <Button
                    variant="success"
                    className="w-100"
                    onClick={() => {
                      const returnUrl =
                        typeof window !== "undefined" && job?.id
                          ? `${window.location.origin}/jobData?jobId=${job.id}&resumeJob=1`
                          : "";
                      const nextParam = returnUrl
                        ? `?next=${encodeURIComponent(returnUrl)}`
                        : "";
                      history.push(`/account/upgrade${nextParam}`);
                    }}
                  >
                    View plan options
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Option 2 — Add Page Pack (one-off) */}
        <Col md={12} lg={4} className="mb-3">
          <Card
            className="h-100"
            style={{ borderWidth: 2, borderColor: "#856404" }}
          >
            <Card.Body>
              <div className="d-flex align-items-center mb-2">
                <span
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mr-2 text-white"
                  style={{
                    width: 28,
                    height: 28,
                    fontSize: 14,
                    backgroundColor: "#856404",
                  }}
                >
                  2
                </span>
                <Card.Title className="mb-0" style={{ color: "#533f03" }}>
                  Add Page Pack
                </Card.Title>
              </div>
              <p className="small text-muted mb-3">
                For one-off spikes. No commitment, pages never expire.
              </p>
              <Button
                variant="outline-dark"
                className="w-100"
                style={{ borderColor: "#856404", color: "#533f03" }}
                onClick={() =>
                  dispatch(
                    openModal("buyPages", {
                      outOfPages: true,
                      returnPath: `${location.pathname}${location.search}${location.search ? "&" : "?"}resumeJob=1`,
                      jobId: job?.id,
                    }),
                  )
                }
              >
                Buy pages
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Option 3 — Flexible Cap (Overage); hidden when trialing (no plan/CC) */}
        {subscription?.status !== "trialing" && (
          <Col md={12} lg={4} className="mb-3">
            <Card className="h-100 border-primary" style={{ borderWidth: 2 }}>
              <Card.Body>
                <div className="d-flex align-items-center mb-2">
                  <span
                    className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center mr-2"
                    style={{ width: 28, height: 28, fontSize: 14 }}
                  >
                    3
                  </span>
                  <Card.Title className="mb-0 text-primary">
                    Extended Limit
                  </Card.Title>
                </div>
                <p className="small text-muted mb-2">
                  Continue at your plan rate with an extended cap. A charge may
                  apply when you enable this.
                </p>
                {overagePricePerPage != null && (
                  <p className="small mb-2">
                    ${overagePricePerPage.toFixed(2)}/page (same as plan rate)
                  </p>
                )}
                {overageConfigLoaded && (
                  <>
                    <p className="small mb-1">
                      Current limit:{" "}
                      {currentOverageLimit === "unlimited"
                        ? "Unlimited"
                        : `${currentOverageLimit}% of plan`}
                    </p>
                    <Form.Group className="mb-2">
                      <Form.Control
                        as="select"
                        value={
                          overageLimit === "unlimited"
                            ? "unlimited"
                            : String(overageLimit)
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          setOverageLimit(
                            v === "unlimited" ? "unlimited" : Number(v),
                          );
                        }}
                        size="sm"
                      >
                        <option value="unlimited">Unlimited</option>
                        <option value="200">200% of plan</option>
                        <option value="300">300% of plan</option>
                      </Form.Control>
                    </Form.Group>
                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={onConfirmOverageAndResume}
                      disabled={confirmingOverage}
                    >
                      {confirmingOverage ? "Saving…" : "Confirm and resume job"}
                    </Button>
                  </>
                )}
                {!overageConfigLoaded && (
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={() =>
                      history.push("/account?section=plan#overage")
                    }
                  >
                    Continue at plan rate with extended limit
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};
